/**
 * Pin Filtering Utilities
 * Provides zoom-based filtering and simple clustering logic
 */

import type { MapPin } from '@/components/maps/MapView';

/**
 * Filter pins based on zoom level
 * At lower zoom levels, show fewer pins to reduce clutter
 */
export const filterPinsByZoom = (
  pins: MapPin[],
  zoomLevel: number,
  options?: {
    showLandmarksAtZoom?: number; // Show landmarks only at this zoom level or higher
    maxPinsAtZoom?: number; // Maximum pins to show at lower zoom levels
    minLandmarksAtMediumZoom?: number; // Keep at least this many landmarks when available
  }
): MapPin[] => {
  const {
    showLandmarksAtZoom = 12,
    maxPinsAtZoom = 50,
    minLandmarksAtMediumZoom = 4,
  } = options || {};

  const hasItineraryPins = pins.some((p) => p.type === 'itinerary');

  // At very low zoom (world view), show only itinerary pins
  if (zoomLevel < 8) {
    // If a trip has no itinerary pins yet, keep landmarks/search visible so users can discover ideas.
    if (!hasItineraryPins) {
      return pins.filter((pin) => pin.type === 'landmark' || pin.type === 'search' || pin.type === 'user');
    }
    return pins.filter(pin => pin.type === 'itinerary' || pin.type === 'user');
  }

  // At medium zoom, show itinerary and user location, but limit landmarks
  if (zoomLevel < showLandmarksAtZoom) {
    const itineraryAndUser = pins.filter(pin => 
      pin.type === 'itinerary' || pin.type === 'user'
    );
    const otherPins = pins.filter(pin => pin.type !== 'itinerary' && pin.type !== 'user');
    // If there are no itinerary pins yet, treat landmarks/search as primary content.
    // Otherwise the map feels empty until the user adds activities.
    if (!hasItineraryPins) {
      const primary = pins.filter((pin) => pin.type === 'landmark' || pin.type === 'search' || pin.type === 'user');
      return primary.slice(0, maxPinsAtZoom);
    }
    
    const roomForOthers = Math.max(0, maxPinsAtZoom - itineraryAndUser.length);
    const landmarks = otherPins.filter((pin) => pin.type === 'landmark');
    const nonLandmarks = otherPins.filter((pin) => pin.type !== 'landmark');

    // Reserve a small landmark floor so discovery pins stay visible while zoomed out.
    const landmarkFloor = Math.min(minLandmarksAtMediumZoom, landmarks.length, roomForOthers);
    const keptLandmarks = landmarks.slice(0, landmarkFloor);

    const remainingRoom = Math.max(0, roomForOthers - keptLandmarks.length);
    const fallbackPool = [...nonLandmarks, ...landmarks.slice(keptLandmarks.length)];
    const limitedOtherPins = [...keptLandmarks, ...fallbackPool.slice(0, remainingRoom)];

    return [...itineraryAndUser, ...limitedOtherPins];
  }

  // At high zoom, show all pins
  return pins;
};

/**
 * Simple distance-based clustering (groups nearby pins)
 * Returns clusters with center coordinates and pin count
 */
export type PinCluster = {
  id: string;
  coordinates: { latitude: number; longitude: number };
  pinCount: number;
  pins: MapPin[];
  type: 'cluster';
};

const EARTH_RADIUS_KM = 6371;
const CLUSTER_DISTANCE_KM = 0.5; // Cluster pins within 500m

function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

/**
 * Cluster nearby pins together
 * Returns both clusters and unclustered pins
 */
export const clusterPins = (
  pins: MapPin[],
  options?: {
    clusterDistance?: number; // Distance in km to cluster pins
    minClusterSize?: number; // Minimum pins to form a cluster
  }
): { clusters: PinCluster[]; unclustered: MapPin[] } => {
  const {
    clusterDistance = CLUSTER_DISTANCE_KM,
    minClusterSize = 2,
  } = options || {};

  if (pins.length < minClusterSize) {
    return { clusters: [], unclustered: pins };
  }

  const processed = new Set<number>();
  const clusters: PinCluster[] = [];
  const unclustered: MapPin[] = [];

  for (let i = 0; i < pins.length; i++) {
    if (processed.has(i)) continue;

    const pin = pins[i];
    const nearbyPins: MapPin[] = [pin];
    const nearbyIndices: number[] = [i];

    // Find nearby pins
    for (let j = i + 1; j < pins.length; j++) {
      if (processed.has(j)) continue;

      const otherPin = pins[j];
      const distance = haversineDistance(
        pin.coordinates.latitude,
        pin.coordinates.longitude,
        otherPin.coordinates.latitude,
        otherPin.coordinates.longitude
      );

      if (distance <= clusterDistance) {
        nearbyPins.push(otherPin);
        nearbyIndices.push(j);
      }
    }

    // If we have enough pins for a cluster, create one
    if (nearbyPins.length >= minClusterSize) {
      // Calculate cluster center (average of all pins)
      const avgLat = nearbyPins.reduce((sum, p) => sum + p.coordinates.latitude, 0) / nearbyPins.length;
      const avgLon = nearbyPins.reduce((sum, p) => sum + p.coordinates.longitude, 0) / nearbyPins.length;

      clusters.push({
        id: `cluster-${i}`,
        coordinates: { latitude: avgLat, longitude: avgLon },
        pinCount: nearbyPins.length,
        pins: nearbyPins,
        type: 'cluster',
      });

      // Mark all pins in cluster as processed
      nearbyIndices.forEach(idx => processed.add(idx));
    } else {
      // Not enough pins for cluster, keep as individual
      unclustered.push(pin);
      processed.add(i);
    }
  }

  return { clusters, unclustered };
};

